import styled from "styled-components";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useAxiosCall from "../../../../hooks/useAxiosCall";
import { PRODUCT_URL } from "../../../../common/constants";
import { DataWrapper } from "../../../../common/components/dataWrapper";
import { ProductDetails } from "../../products/subs/product-details";
import { useAuthDetails } from "../../../../hooks/useAuthDetails";
import { useApiCallHandling } from "../../../../hooks/useApiCallHandling";

import { ProductFactory } from "../../common/product-factory";
import { KeyValueObj } from "../../../../common/types";
import { RiErrorWarningFill } from "react-icons/ri";

// MED-711: which of the two reasons the buyer is being given. Seller-scoped and
// platform-wide need different wording — "this supplier" is wrong when the answer
// is about every supplier on the platform, which is what an unscoped link gets.
function unavailableReason(reason: string | undefined, sellerScoped: boolean) {
  if (!sellerScoped) {
    return "The suppliers listing it do not currently hold enough stock to fulfil an order.";
  }

  return reason === "not_listed_for_sale"
    ? "This supplier does not sell this product."
    : "This supplier does not currently have enough stock to fulfil an order.";
}

const ProductDetailsWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 3rem 0 4rem;
  flex-wrap: wrap;

  .loading-text {
    /* MED-711: was theme.button.primary, a token whose whole group is commented
       out of the theme, so reading it threw and the page never rendered at all. */
    color: ${({ theme }) => theme.text.progress};
    font-size: 1.2rem;
    font-weight: 400;
  }

  .carousel-wrapper {
    width: 50%;
  }
  .details {
    width: 50%;
    padding: 0 3rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;

    .top {
      margin-bottom: 1.5rem;

      .brand {
        font-size: 3rem;
        font-weight: 600;
        color: ${({ theme }) => theme.text.primary};
      }
      .name {
        color: ${({ theme }) => theme.text.input};
        font-weight: 400;
        font-size: 1.4rem;
      }
    }

    .further-details {
      width: 100%;
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
    }

    .item {
      min-width: 45%;

      .title {
        color: ${({ theme }) => theme.text.muted_200};
        font-weight: 400;
        font-size: 1.2rem;
      }

      .value {
        color: ${({ theme }) => theme.text.input};
        font-weight: 500;
        font-size: 1.2rem;
        text-transform: capitalize;
      }
    }
  }

  /* MED-704: matches the MED-651 edit-blocked notice in
     products/subs/product-details/style.ts — same job, same visual language. */
  .unavailable {
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    padding: 0.85rem 1rem;
    border-radius: 0.25rem;
    border-left: 4px solid ${({ theme }) => theme.bg.warn};
    /* 1A = ~10% alpha on the warn colour; the theme has no warning tint token. */
    background-color: ${({ theme }) => theme.bg.warn}1a;

    .icon {
      flex-shrink: 0;
      margin-top: 0.1rem;
      font-size: 1.1rem;
      color: ${({ theme }) => theme.bg.warn};
    }

    .lead {
      font-size: 0.9rem;
      font-weight: 600;
      color: ${({ theme }) => theme.text.primary};
    }

    .reason {
      margin-top: 0.25rem;
      font-size: 0.85rem;
      color: ${({ theme }) => theme.text.prim_50};
    }

    .back {
      margin-top: 0.6rem;
      font-size: 0.85rem;
      font-weight: 500;
      text-decoration: underline;
      color: ${({ theme }) => theme.text.secondary};
    }
  }
`;

export interface IProductDetailsProps {}

export function ProductDetailsPage(_: IProductDetailsProps) {
  const [retry, setRetry] = useState(0);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editProduct, setEditProduct] = useState(false);
  const [product, setProduct] = useState<KeyValueObj>({});

  const { id } = useParams();

  // MED-711: the seller this link was scoped to, if any. Buyers reach this page
  // by a shared or bookmarked URL, so the query string is the only place a
  // seller can come from — there is no in-app navigation to this route.
  const [searchParams] = useSearchParams();
  const sellerId = searchParams.get("company_id");

  //CUSTOM HOOKES
  const [fetchProduct, fetchedProduct, productError, productLoading] =
    useAxiosCall();
  const { companyId } = useAuthDetails();
  const apiCall = useApiCallHandling();
  const navigate = useNavigate();

  useEffect(() => {
    const query = sellerId ? `?company_id=${encodeURIComponent(sellerId)}` : "";
    fetchProduct({ url: PRODUCT_URL + "/" + id + query, method: "GET" });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, retry, sellerId]);

  useEffect(() => {
    setProduct(fetchedProduct);
  }, [fetchedProduct]);

  function handleEditProduct() {
    setEditProduct(true);
  }

  function deleteProduct() {
    setDeleteLoading(true);
    apiCall(
      PRODUCT_URL + "/" + id,
      "DELETE",
      {},
      // @ts-ignore
      (resp: any) => {
        navigate("/dashboard/products");
      },
      (error: any) => {
        console.log(error);
      },
      () => {
        setDeleteLoading(false);
      }
    );
  }

  function updateProductInfo(data: KeyValueObj, done: boolean) {
    setProduct(data);
    done && setEditProduct(false);
  }

  return (
    <ProductDetailsWrapper>
      <ProductFactory
        open={editProduct}
        close={() => {
          setEditProduct(false);
        }}
        editMode
        product={product}
        onSuccessfullMutation={updateProductInfo}
        // Was hardcoded to "drug", which mis-types a medical item on save.
        productType={product?.product_type || "drug"}
      />
      <DataWrapper
        data={product}
        noDataMessage={"No product to show"}
        loading={productLoading}
        error={productError}
        loadingMessage="Loading products data..."
        failedMessage="Could not fetch products data. Check your network and click the button below to retry"
        onFailAction={() => {
          setRetry((prev) => prev + 1);
        }}
      >
        <>
          {/* MED-704: the product exists, so a buyer reaching it by a direct link
              or a stale page is told why they cannot order it rather than being
              sent to a not-found page. Compared against `false` explicitly: the
              field is only sent on the detail response, and an absent one must
              read as purchasable.

              Owner check mirrors isOwnerView in the ProductDetails component
              below: a company opening its own product is not being sold to. The
              backend also withholds the field from the owner; this is the
              second half of the same rule, not a substitute for it. */}
          {product?.owner_id !== companyId &&
            product?.purchase_availability?.purchasable === false && (
            <div className="unavailable" role="status">
              <RiErrorWarningFill className="icon" aria-hidden="true" />
              <div>
                <p className="lead">
                  This product is no longer available to order
                </p>
                <p className="reason">
                  {unavailableReason(
                    product?.purchase_availability?.reason,
                    !!sellerId
                  )}
                </p>
                <button
                  type="button"
                  className="back"
                  onClick={() => navigate("/dashboard/products")}
                >
                  Back to catalogue
                </button>
              </div>
            </div>
          )}
          <ProductDetails
            deleteLoading={deleteLoading}
            companyId={companyId}
            product={product}
            handleDelete={deleteProduct}
            handleEdit={handleEditProduct}
          />
        </>
      </DataWrapper>
    </ProductDetailsWrapper>
  );
}
